// apps/codeframe/src/chrome/timeline/timeline-store.ts
import { create } from 'zustand';

const OPEN_KEY = 'codeframe.timeline.open';
const HEIGHT_KEY = 'codeframe.timeline.height';

export const MIN_HEIGHT = 160;
export const MAX_HEIGHT = 520;
export const DEFAULT_HEIGHT = 240;

/** Clamp the dock height to the sane resize range. Pure — unit-tested. */
export function clampHeight(n: number): number {
  if (Number.isNaN(n)) return DEFAULT_HEIGHT;
  return Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, Math.round(n)));
}

function readOpen(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(OPEN_KEY) === '1';
}
function readHeight(): number {
  if (typeof localStorage === 'undefined') return DEFAULT_HEIGHT;
  const v = Number(localStorage.getItem(HEIGHT_KEY));
  return v ? clampHeight(v) : DEFAULT_HEIGHT;
}
function persist(key: string, value: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, value);
  } catch {
    /* quota / disabled */
  }
}

/** Identifies a single keyframe for selection / easing-popover targeting. */
export interface KfRef {
  trackId: string;
  layerId: string;
  prop: string;
  t: number;
}

interface TimelineStore {
  open: boolean;
  height: number;
  /** Record mode: drags of canvas nodes write keyframes. Ephemeral (never persisted). */
  recording: boolean;
  /** Currently selected keyframe (for value/ease editing). Ephemeral. */
  selectedKf: KfRef | null;
  /** Anchor for the easing popover: a segment's leading keyframe + screen pos. */
  easingFor: { ref: KfRef; x: number; y: number } | null;
  toggle: () => void;
  setOpen: (open: boolean) => void;
  setHeight: (h: number) => void;
  setRecording: (v: boolean) => void;
  selectKf: (ref: KfRef | null) => void;
  openEasing: (ref: KfRef, x: number, y: number) => void;
  closeEasing: () => void;
}

export const useTimelineStore = create<TimelineStore>((set) => ({
  open: readOpen(),
  height: readHeight(),
  recording: false,
  selectedKf: null,
  easingFor: null,
  toggle: () =>
    set((s) => {
      const open = !s.open;
      persist(OPEN_KEY, open ? '1' : '0');
      return { open };
    }),
  setOpen: (open) => {
    persist(OPEN_KEY, open ? '1' : '0');
    set({ open });
  },
  setHeight: (h) => {
    const v = clampHeight(h);
    persist(HEIGHT_KEY, String(v));
    set({ height: v });
  },
  setRecording: (v) => set({ recording: v }), // ephemeral — intentionally NOT persisted
  selectKf: (ref) => set({ selectedKf: ref }),
  openEasing: (ref, x, y) => set({ easingFor: { ref, x, y } }),
  closeEasing: () => set({ easingFor: null }),
}));
